/**
 * Anthra AI — Frontend error handling framework
 *
 * The client half of the contract in API_STYLE_GUIDE.md §5. Because every service
 * returns the same error shape, this is written once and every feature reuses it.
 *
 * THE THREE RULES
 *   1. Branch on `code`, NEVER on `message`. Messages are copy and will change;
 *      codes are part of the API contract and changing one is a breaking change.
 *   2. Always surface the correlationId to the user on an unexpected failure. It
 *      turns "it broke" into a one-query support ticket (OBSERVABILITY.md §2).
 *   3. Never render a raw error object. `String(err)` on a network failure
 *      produces something meaningless to a user and alarming to a client.
 */

import { z } from "zod"

import { ERROR_CODES, ERROR_MESSAGES, type ErrorCode, lockoutMessage, secondsUntil } from "./codes"

// The domain codes are part of this module's public face — a caller branching on
// `code` imports both from one place.
export { ERROR_CODES, ERROR_MESSAGES, lockoutMessage, secondsUntil }
export type { ErrorCode }

/**
 * The wire shape every Anthra AI service returns for 4xx and 5xx.
 *
 * TWO SPELLINGS OF ONE IDEA, AND BOTH ARE ACCEPTED. The scaffold's envelope
 * names the trace handle `correlationId`; the approved architecture for this
 * product names it `request_id` and adds a free-form `meta`. Rather than pick
 * one and be wrong against the other, the schema reads both and `fromResponse`
 * collapses them onto the single `correlationId` field the rest of this package
 * — the logger, the analytics client, `DisplayableError` — already speaks.
 *
 * Getting this wrong is not a crash, which is why it is worth stating: a client
 * that only read `correlationId` against a service that only sends `request_id`
 * would work perfectly and silently drop the one value that joins a user's "it
 * broke" to the server-side trace.
 */
export const apiErrorSchema = z.object({
	error: z.object({
		code: z.string(),
		message: z.string(),
		correlationId: z.string().optional(),
		/** The architecture's spelling of the same value. */
		request_id: z.string().optional(),
		/**
		 * The per-field failures, which the service calls `fields`.
		 *
		 * BOTH SPELLINGS, for the same reason `request_id` is read beside
		 * `correlationId`: the scaffold's envelope says `details` and the
		 * deployed service says `fields`, and a client that read only the first
		 * showed a form with a generic banner and no message against the field
		 * that actually failed. `fromResponse` collapses them onto `details`,
		 * which is what `fieldErrors` and every form already read.
		 */
		details: z
			.array(
				z.object({
					field: z.string(),
					code: z.string(),
					message: z.string(),
				}),
			)
			.optional(),
		fields: z
			.array(
				z.object({
					field: z.string(),
					code: z.string(),
					message: z.string(),
				}),
			)
			.optional(),
		/**
		 * Structured context the service chose to attach — a remaining lockout,
		 * a limit that was exceeded. Deliberately untyped: it is diagnostic, and
		 * a client that branched on it would be branching on something other
		 * than `code`.
		 */
		meta: z.record(z.unknown()).optional(),
		docsUrl: z.string().optional(),
		/**
		 * The published contract's own spelling (`Error.error.docs_url` in the
		 * service's OpenAPI document). `ApiError.fromResponse` reads the error
		 * body straight off `response.json()` — the snake_case→camelCase
		 * conversion in ../api/client runs only on the success path, so the wire
		 * spelling has to be accepted here directly rather than assumed already
		 * converted.
		 */
		docs_url: z.string().optional(),
		retryable: z.boolean().optional(),
	}),
})

export type ApiErrorBody = z.infer<typeof apiErrorSchema>
export type FieldError = NonNullable<ApiErrorBody["error"]["details"]>[number]

/**
 * The single error type the whole application handles.
 *
 * Anything thrown by the API client is one of these, so a component never has to
 * ask "what kind of thing is this?"
 */
export class ApiError extends Error {
	readonly status: number
	readonly code: string
	readonly correlationId: string | undefined
	readonly details: FieldError[]
	readonly docsUrl: string | undefined
	readonly retryable: boolean
	/**
	 * Seconds from `Retry-After`, when the response carried one.
	 *
	 * Carried on the error rather than left in the response because the two
	 * places it matters — the OTP lockout countdown and the resend cooldown —
	 * are both several layers above the fetch, and a header read at the call
	 * site would mean reading it in three apps (US-00-001.2, US-01-005.9).
	 */
	readonly retryAfterSeconds: number | undefined
	/**
	 * The envelope's `meta`, for a log line or a bug report.
	 *
	 * NOT for branching — that is `code`'s job, and a screen that reached into
	 * `meta` would be depending on a field no other endpoint promises.
	 */
	readonly meta: Record<string, unknown>

	constructor(init: {
		status: number
		code: string
		message: string
		correlationId?: string
		details?: FieldError[]
		meta?: Record<string, unknown>
		docsUrl?: string
		retryable?: boolean
		retryAfterSeconds?: number
	}) {
		super(init.message)
		this.name = "ApiError"
		this.status = init.status
		this.code = init.code
		this.correlationId = init.correlationId
		this.details = init.details ?? []
		this.meta = init.meta ?? {}
		this.docsUrl = init.docsUrl
		this.retryable = init.retryable ?? false
		this.retryAfterSeconds = init.retryAfterSeconds
	}

	/** Builds an ApiError from a Response, tolerating a body that is not our shape. */
	static async fromResponse(response: Response): Promise<ApiError> {
		const correlationId = correlationIdFrom(response.headers)
		const retryAfterSeconds = parseRetryAfter(response.headers.get("Retry-After"))

		let body: unknown
		try {
			body = await response.json()
		} catch {
			// A gateway timeout or a proxy error page is not JSON. Do not let the
			// parse failure mask the real status.
			return new ApiError({
				status: response.status,
				code: fallbackCode(response.status),
				message: fallbackMessage(response.status),
				correlationId,
				retryable: isRetryableStatus(response.status),
				retryAfterSeconds,
			})
		}

		const parsed = apiErrorSchema.safeParse(body)
		if (!parsed.success) {
			return new ApiError({
				status: response.status,
				code: fallbackCode(response.status),
				message: fallbackMessage(response.status),
				correlationId,
				retryable: isRetryableStatus(response.status),
				retryAfterSeconds,
			})
		}

		const { error } = parsed.data
		return new ApiError({
			status: response.status,
			code: error.code,
			message: error.message,
			// Body first, header second: the header is set at the edge and the
			// body by the handler that actually produced the failure, so where
			// they differ the body is the one that names the right log line.
			correlationId: error.correlationId ?? error.request_id ?? correlationId,
			details: error.details ?? error.fields ?? [],
			meta: error.meta ?? {},
			docsUrl: error.docsUrl ?? error.docs_url,
			retryable: error.retryable ?? isRetryableStatus(response.status),
			retryAfterSeconds,
		})
	}

	get isUnauthenticated(): boolean {
		return this.status === 401
	}

	get isForbidden(): boolean {
		return this.status === 403
	}

	get isNotFound(): boolean {
		return this.status === 404
	}

	get isConflict(): boolean {
		return this.status === 409
	}

	get isValidation(): boolean {
		return this.status === 422
	}

	get isRateLimited(): boolean {
		return this.status === 429
	}

	/** Our fault. Worth showing the correlation ID and a "contact support" path. */
	get isServerError(): boolean {
		return this.status >= 500
	}

	/**
	 * Field errors keyed by path, for handing straight to a form library:
	 *   Object.entries(err.fieldErrors).forEach(([f, m]) => setError(f, { message: m }))
	 */
	get fieldErrors(): Record<string, string> {
		return Object.fromEntries(this.details.map(d => [d.field, d.message]))
	}
}

/** Thrown when the network never produced a response. Distinct from a 5xx. */
export class NetworkError extends Error {
	readonly retryable = true

	constructor(cause?: unknown) {
		super("Unable to reach the server. Check your connection and try again.")
		this.name = "NetworkError"
		this.cause = cause
	}
}

/**
 * Thrown when a response parsed as JSON but did not match its Zod schema.
 *
 * This is a CONTRACT VIOLATION, not a user problem — the server sent something
 * the client does not understand, which usually means a deploy skew or an
 * undocumented API change. It is surfaced loudly rather than swallowed, because
 * silently coercing it is how a `undefined is not an object` crash appears three
 * screens later with no clue where it came from.
 */
export class SchemaError extends Error {
	readonly issues: z.ZodIssue[]
	readonly endpoint: string

	constructor(endpoint: string, issues: z.ZodIssue[]) {
		super(`The server response did not match the expected shape for ${endpoint}.`)
		this.name = "SchemaError"
		this.endpoint = endpoint
		this.issues = issues
	}
}

/**
 * The trace handle a response carries, under either header name.
 *
 * `X-Correlation-ID` is what the guidelines specify is echoed; `X-Request-Id` is
 * what a Go service fronted by a load balancer commonly sets. Reading both here
 * means ../api/client and this module bind the identical value, rather than a
 * success being joinable to the server trace and a failure not.
 */
export function correlationIdFrom(headers: Headers): string | undefined {
	return headers.get("X-Correlation-ID") ?? headers.get("X-Request-Id") ?? undefined
}

function fallbackCode(status: number): string {
	if (status === 401) return "unauthenticated"
	if (status === 403) return "forbidden"
	if (status === 404) return "not_found"
	if (status === 429) return "rate_limited"
	if (status >= 500) return "internal_error"
	return "request_failed"
}

function fallbackMessage(status: number): string {
	if (status === 401) return "Your session has expired. Please sign in again."
	if (status === 403) return "You do not have permission to do this."
	if (status === 404) return "We could not find what you were looking for."
	if (status === 429) return "Too many requests. Please wait a moment and try again."
	if (status >= 500) return "Something went wrong on our side. Please try again."
	return "The request could not be completed."
}

function isRetryableStatus(status: number): boolean {
	return status === 408 || status === 429 || status === 502 || status === 503 || status === 504
}

/**
 * `Retry-After` in seconds.
 *
 * RFC 9110 allows either a delta in seconds or an HTTP date, and real gateways
 * send both. A date in the past yields 0 rather than a negative, so a caller can
 * treat the value as "wait this long" without guarding.
 */
function parseRetryAfter(header: string | null): number | undefined {
	if (!header) return undefined
	const seconds = Number(header)
	if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds))
	const date = Date.parse(header)
	if (Number.isNaN(date)) return undefined
	return Math.max(0, Math.ceil((date - Date.now()) / 1000))
}

// -----------------------------------------------------------------------------
// Presentation
// -----------------------------------------------------------------------------

export type DisplayableError = {
	title: string
	message: string
	/** Shown as "Reference: <id>" so a user can quote it to support. */
	correlationId?: string
	canRetry: boolean
	/** Field errors, when the caller wants to attach them to inputs. */
	fields: Record<string, string>
}

/**
 * Converts anything thrown into something safe to render.
 *
 * Use this in every catch that reaches the UI. It guarantees a user never sees a
 * raw stack, an unhandled `[object Object]`, or an internal detail.
 */
export function toDisplayable(error: unknown): DisplayableError {
	if (error instanceof ApiError) {
		return {
			title: titleForStatus(error.status),
			// The domain copy wins where there is any, because several acceptance
			// criteria name the exact wording a user must see and three clients
			// have to show the same one. Anything not in the table falls through
			// to the server's message, which is where a newly-added code lands.
			message: domainMessage(error) ?? error.message,
			// Only surface the reference where it helps: on our failures, not on a
			// validation error the user can fix themselves.
			...(error.isServerError && error.correlationId
				? { correlationId: error.correlationId }
				: {}),
			canRetry: error.retryable,
			fields: error.fieldErrors,
		}
	}

	if (error instanceof NetworkError) {
		return {
			title: "Connection problem",
			message: error.message,
			canRetry: true,
			fields: {},
		}
	}

	if (error instanceof SchemaError) {
		return {
			title: "Unexpected response",
			message:
				"The server sent something we did not expect. Our team has been notified. Please try again.",
			canRetry: true,
			fields: {},
		}
	}

	// Deliberately generic. An unexpected exception's message is written for a
	// developer and frequently contains internals.
	return {
		title: "Something went wrong",
		message: "An unexpected error occurred. Please try again.",
		canRetry: true,
		fields: {},
	}
}

/**
 * The product's own copy for a recognised code, or undefined.
 *
 * The lockout is special-cased so the message can carry the remaining time from
 * `Retry-After` rather than repeating the configured thirty minutes at a user
 * who has already waited twenty of them.
 */
function domainMessage(error: ApiError): string | undefined {
	if (error.code === ERROR_CODES.otpLocked) return lockoutMessage(error.retryAfterSeconds)
	return ERROR_MESSAGES[error.code]
}

function titleForStatus(status: number): string {
	if (status === 401) return "Session expired"
	if (status === 403) return "Not allowed"
	if (status === 404) return "Not found"
	if (status === 409) return "Conflict"
	if (status === 422) return "Check your details"
	if (status === 429) return "Slow down"
	if (status >= 500) return "Something went wrong"
	return "Request failed"
}

/**
 * The retry policy for TanStack Query. Import it into the QueryClient defaults so
 * every query behaves the same way.
 *
 * Retrying a 4xx is pointless — the same request will fail identically, and for a
 * 429 it actively makes things worse.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
	if (failureCount >= 3) return false
	if (error instanceof NetworkError) return true
	if (error instanceof SchemaError) return false
	if (error instanceof ApiError) {
		if (error.status === 429) return false // respect the limit; do not hammer it
		return error.status >= 500
	}
	return false
}

/** Exponential backoff with a ceiling, for the QueryClient's retryDelay. */
export function retryDelay(attempt: number): number {
	return Math.min(1000 * 2 ** attempt, 30_000)
}

// -----------------------------------------------------------------------------
// Domain predicates
//
// Each of these answers a question a screen actually asks, so that no screen has
// to know a status code and a string literal at the same time. They take
// `unknown` because that is what a catch block has.
// -----------------------------------------------------------------------------

function hasCode(error: unknown, code: string): error is ApiError {
	return error instanceof ApiError && error.code === code
}

/** The number is locked out. The screen shows the message and hides the inputs. */
export function isOtpLockout(error: unknown): error is ApiError {
	return hasCode(error, ERROR_CODES.otpLocked)
}

/**
 * The code was issued too long ago.
 *
 * Distinct from a wrong code because the failed-attempt counter is NOT
 * incremented for it (US-00-001.8) — so the screen keeps the attempts
 * indicator where it was and offers a resend instead.
 */
export function isOtpExpired(error: unknown): error is ApiError {
	return hasCode(error, ERROR_CODES.otpExpired)
}

/** The code was wrong. One of the three attempts has been used, server-side. */
export function isOtpIncorrect(error: unknown): error is ApiError {
	return hasCode(error, ERROR_CODES.otpInvalid)
}

/**
 * WhatsApp could not be reached after its retries.
 *
 * The screen shows 'could not send the code, try again' and leaves the resend
 * control ENABLED, which is the part that is easy to get wrong (US-00-001.22).
 */
export function isOtpDispatchFailure(error: unknown): error is ApiError {
	return hasCode(error, ERROR_CODES.otpDispatchFailed)
}

/**
 * The session is gone: expired, revoked, tampered with, or belonging to an
 * Expert who has just been deactivated.
 *
 * Every one of them means the same thing to a client — clear the local token,
 * hold the destination, go to OTP sign-in (US-00-006.10, US-00-006.21).
 */
export function isSessionInvalid(error: unknown): boolean {
	if (!(error instanceof ApiError)) return false
	// A deactivated Expert is refused with the same `session_invalid` on their
	// very next call as a session that simply ran out (US-01-005.17), which is
	// why there is no separate code to test for here.
	return error.status === 401 || error.code === ERROR_CODES.sessionExpired
}

/**
 * Someone else's record, or a record that does not exist.
 *
 * Deliberately one predicate, because the API deliberately returns one response
 * for both (US-00-006.19). A caller that wants to distinguish them cannot, and
 * that is the feature.
 */
export function isAccessDenied(error: unknown): boolean {
	if (!(error instanceof ApiError)) return false
	return error.status === 403 || error.code === ERROR_CODES.accessDenied
}

/**
 * The record has moved on: closed, already rated, already reviewed, or in a
 * status the requested transition is not valid from.
 *
 * Always a 409 (US-00-002.12/.14, US-01-003.6/.7/.8, US-01-004.11).
 */
export function isLifecycleConflict(error: unknown): error is ApiError {
	return error instanceof ApiError && error.status === 409
}

/** A server-side upload rejection — sniffed type, size, or count. */
export function isUploadRejection(error: unknown): error is ApiError {
	return hasCode(error, ERROR_CODES.invalidFile) || hasCode(error, ERROR_CODES.tooManyFiles)
}

/**
 * The request never reached the server.
 *
 * The reason it matters is not the copy: an OTP verification that never arrived
 * has consumed NO attempt, because attempts are counted server-side only
 * (US-00-001.21). A screen that treats a connectivity failure as a wrong code
 * shows a user two attempts remaining when they have three.
 */
export function isConnectivityFailure(error: unknown): error is NetworkError {
	return error instanceof NetworkError
}
